// The console's session as an `ApiTransport` (packages/api-client), so the shared typed APIs run
// here as they do in the desktop: `CampaignsApi` for brief change cards and members, `BriefsApi`
// for the brief interview. Requests carry the session cookie and the CSRF header.

import { type ApiTransport, BriefsApi, CampaignsApi, parseSse } from "@vd/api-client";
import { API_BASE } from "../config";
import { ApiError, api, csrfHeader } from "./client";
import type { ApiErrorBody } from "./types";

/** POSTs `body` and yields the server-sent events of the reply, one JSON object each. */
async function* streamEvents<E>(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<E> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { accept: "text/event-stream", "content-type": "application/json", ...csrfHeader() },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Can't reach the server. Check your connection and try again.");
  }
  if (!res.ok) {
    let code = "http_error";
    let message = `The server returned ${res.status}.`;
    try {
      const b = (await res.json()) as ApiErrorBody;
      if (b?.error) {
        code = b.error.code;
        message = b.error.message;
      }
    } catch {
      // Not JSON; keep the generic words.
    }
    throw new ApiError(res.status, code, message);
  }
  const parse = parseSse as unknown as (raw: string) => Generator<E>;
  if (!res.body) {
    yield* parse(await res.text());
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      buf += decoder.decode(chunk.value, { stream: true }).replace(/\r\n/g, "\n");
      let cut = buf.indexOf("\n\n");
      while (cut >= 0) {
        yield* parse(buf.slice(0, cut));
        buf = buf.slice(cut + 2);
        cut = buf.indexOf("\n\n");
      }
    }
    if (buf.trim()) yield* parse(buf);
  } finally {
    reader.releaseLock();
  }
}

export const transport: ApiTransport = {
  get: <T>(path: string, signal?: AbortSignal) => api<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: "PATCH", body }),
  del: <T>(path: string) => api<T>(path, { method: "DELETE" }),
  streamEvents,
};

/** Briefs, change cards, members and conversations through the shared client. */
export const shared = new CampaignsApi(transport);
/** The brief interview, "Help me make one". */
export const briefs = new BriefsApi(transport);
